import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNetwork } from '@/providers/NetworkProvider';
import '@/styles/hacker-aesthetic.css';
import '@/styles/daily.css';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

interface DailyQuest {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  progress: number;
  target: number;
  type: string;
}

interface DailyStats {
  nodesPurchased: number;
  chestsOpened: number;
  upgradesApplied: number;
  rewardsClaimed: number;
}

interface DailyRewardsPanelProps {
  onClose: () => void;
}

const emptyStats = (): DailyStats => ({
  nodesPurchased: 0,
  chestsOpened: 0,
  upgradesApplied: 0,
  rewardsClaimed: 0,
});

const utcDateKey = () => new Date().toISOString().slice(0, 10);

const storageKeyFor = (wallet: string) =>
  `nodeEmpire_dailyQuests_${utcDateKey()}_${wallet.toLowerCase()}`;

const readStoredStats = (wallet: string): DailyStats => {
  try {
    const raw = localStorage.getItem(storageKeyFor(wallet));
    if (!raw) return emptyStats();
    const parsed = JSON.parse(raw);
    return {
      nodesPurchased: Number(parsed.nodesPurchased) || 0,
      chestsOpened: Number(parsed.chestsOpened) || 0,
      upgradesApplied: Number(parsed.upgradesApplied) || 0,
      rewardsClaimed: Number(parsed.rewardsClaimed) || 0,
    };
  } catch {
    return emptyStats();
  }
};

const writeStoredStats = (wallet: string, stats: DailyStats) => {
  try {
    localStorage.setItem(storageKeyFor(wallet), JSON.stringify(stats));
  } catch {
    /* ignore quota */
  }
};

export const DailyRewardsPanel: React.FC<DailyRewardsPanelProps> = ({ onClose }) => {
  const { isConnected, contracts, signer, address } = useNetwork();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [quests, setQuests] = useState<DailyQuest[]>([]);

  const walletRef = useRef<string | null>(null);

  const generateDailyQuests = useCallback((stats: DailyStats) => {
    const dailyQuests: DailyQuest[] = [
      {
        id: 'daily-node',
        title: t('daily.node_owner'),
        description: t('daily.node_desc'),
        completed: stats.nodesPurchased >= 1,
        progress: Math.min(stats.nodesPurchased, 5),
        target: 1,
        type: 'purchase',
      },
      {
        id: 'daily-chest',
        title: t('daily.chest_owner'),
        description: t('daily.chest_desc'),
        completed: stats.chestsOpened >= 3,
        progress: Math.min(stats.chestsOpened, 10),
        target: 3,
        type: 'chest',
      },
      {
        id: 'daily-upgrade',
        title: t('daily.upgrade_owner'),
        description: t('daily.upgrade_desc'),
        completed: stats.upgradesApplied >= 2,
        progress: Math.min(stats.upgradesApplied, 5),
        target: 2,
        type: 'upgrade',
      },
      {
        id: 'daily-claim',
        title: t('daily.reward_collector'),
        description: t('daily.reward_desc'),
        completed: stats.rewardsClaimed >= 1,
        progress: Math.min(stats.rewardsClaimed, 3),
        target: 1,
        type: 'claim',
      },
    ];

    setQuests(dailyQuests);
  }, [t]);

  const persistAndApply = useCallback(
    (next: DailyStats) => {
      const wallet = walletRef.current;
      if (wallet) writeStoredStats(wallet, next);
      generateDailyQuests(next);
    },
    [generateDailyQuests]
  );

  useEffect(() => {
    const loadLocalProgress = async () => {
      if (!isConnected || !signer) {
        setLoading(false);
        setQuests([]);
        return;
      }

      try {
        setLoading(true);
        const wallet = (address || (await signer.getAddress())).toLowerCase();
        walletRef.current = wallet;
        const stats = readStoredStats(wallet);
        persistAndApply(stats);
      } catch (error) {
        console.error('Failed to load daily quest progress:', error);
      } finally {
        setLoading(false);
      }
    };

    void loadLocalProgress();
  }, [isConnected, signer, address, persistAndApply]);

  const bumpStat = useCallback(
    (key: keyof DailyStats) => {
      const wallet = walletRef.current;
      if (!wallet) return;
      const prev = readStoredStats(wallet);
      const next = { ...prev, [key]: prev[key] + 1 };
      persistAndApply(next);
    },
    [persistAndApply]
  );

  useEffect(() => {
    if (!isConnected || !contracts?.gameLogic || !signer) return undefined;

    const gameLogic = contracts.gameLogic;
    const matchesPlayer = (player: string) => {
      const wallet = walletRef.current || signer.address;
      return Boolean(wallet && player?.toLowerCase() === wallet.toLowerCase());
    };

    const handleNodePurchase = (player: string) => {
      if (matchesPlayer(player)) bumpStat('nodesPurchased');
    };
    const handleChestOpen = (player: string) => {
      if (matchesPlayer(player)) bumpStat('chestsOpened');
    };
    const handleUpgradeApplied = (player: string) => {
      if (matchesPlayer(player)) bumpStat('upgradesApplied');
    };
    const handleRewardsClaimed = (player: string) => {
      if (matchesPlayer(player)) bumpStat('rewardsClaimed');
    };

    try {
      gameLogic.on('NodePurchased', handleNodePurchase);
      gameLogic.on('ChestOpened', handleChestOpen);
      gameLogic.on('UpgradeApplied', handleUpgradeApplied);
      gameLogic.on('RewardsClaimed', handleRewardsClaimed);
    } catch (error) {
      console.error('Failed to set up daily listeners:', error);
    }

    return () => {
      try {
        gameLogic.off('NodePurchased', handleNodePurchase);
        gameLogic.off('ChestOpened', handleChestOpen);
        gameLogic.off('UpgradeApplied', handleUpgradeApplied);
        gameLogic.off('RewardsClaimed', handleRewardsClaimed);
      } catch (error) {
        console.error('Failed to cleanup daily listeners:', error);
      }
    };
  }, [isConnected, contracts, signer, bumpStat]);

  const markQuestSeen = async (questId: string) => {
    try {
      const quest = quests.find((q) => q.id === questId);
      if (!quest || !quest.completed) {
        toast('Habit tracker only — no ETCPOWv2 payout from Daily Quests.');
        return;
      }
      toast.success('Streak noted — local progress only, no chain claim');
    } catch (error) {
      console.error('Failed to update quest:', error);
      toast.error('Failed to update quest. Please try again.');
    }
  };

  const msUntilUtcMidnight = () => {
    const now = new Date();
    const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
    return next.getTime() - now.getTime();
  };

  const resetLabel = () => {
    const ms = msUntilUtcMidnight();
    const hours = Math.floor(ms / 3_600_000);
    const minutes = Math.floor((ms % 3_600_000) / 60_000);
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="dq-panel">
      <div className="dq-header">
        <div className="dq-heading">
          <span className="dq-offchain-badge">LOCAL / OFFCHAIN</span>
          <div className="dq-title">{t('daily.title')}</div>
          <div className="dq-subtitle">Streak habit tool · no faucet</div>
        </div>
        <div className="dq-header-right">
          <span className="dq-reset-chip">
            Resets in {resetLabel()} (UTC)
          </span>
          <button className="dq-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
      </div>

      <div className="dq-info-strip">
        <svg className="dq-info-icon" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <circle cx="8" cy="8" r="6.5" />
          <path d="M8 7.5v3.5" strokeLinecap="round" />
          <path d="M8 5.2v.1" strokeLinecap="round" />
        </svg>
        <span>
          Local streak tracker only — does <strong>not</strong> pay ETCPOWv2. Counts today&apos;s activity for this wallet.
          Real earnings come from activating nodes and claiming on-chain rewards.
        </span>
      </div>

      {loading ? (
        <div className="loading-overlay">
          <div className="hacker-loading"></div>
          <span>{t('daily.loading_quests')}</span>
        </div>
      ) : (
        <div className="dq-body">
          <div className="dq-grid">
            {quests.map((quest) => (
              <div key={quest.id} className={`dq-card ${quest.completed ? 'is-done' : ''}`}>
                <div className="dq-card-title">{quest.title}</div>
                <div className="dq-card-desc">{quest.description}</div>

                <div className="dq-progress">
                  <div className="dq-progress-track">
                    <div className="dq-progress-fill" style={{ width: `${(quest.progress / quest.target) * 100}%` }}></div>
                  </div>
                  <span className="dq-progress-count">{quest.progress}/{quest.target}</span>
                </div>

                <button
                  className={`dq-status-btn ${quest.completed ? 'done' : ''}`}
                  onClick={() => markQuestSeen(quest.id)}
                  disabled={!quest.completed}
                >
                  {quest.completed ? 'TRACKED' : 'IN PROGRESS'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
